import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import RawHeader from "../components/RawHeader";
import { InfoFooter } from "./InfoPages";
import { useCustomer } from "../contexts/CustomerContext";
import { customerApi, logoutCustomer } from "../lib/customerApi";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;
const date = (s) => (s ? new Date(s).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

const MyAccount = () => {
  const { customer, loading } = useCustomer();
  const [orders, setOrders] = useState([]);
  const [saved, setSaved] = useState([]);
  const [tab, setTab] = useState("orders");

  useEffect(() => {
    if (!customer) return;
    customerApi.get("/my/orders").then((r) => setOrders(r.data)).catch(() => {});
    customerApi.get("/my/saved").then((r) => setSaved(r.data)).catch(() => {});
  }, [customer]);

  if (loading) return null;
  if (!customer) return <Navigate to="/login" replace />;

  const unsave = async (pid) => {
    try {
      await customerApi.delete(`/my/saved/${pid}`);
      setSaved((s) => s.filter((x) => x.id !== pid));
      toast.success("Removed from saved items");
    } catch {
      toast.error("Failed to remove");
    }
  };

  return (
    <div data-testid="account-page" className="min-h-screen text-white">
      <RawHeader variant="public" />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-center gap-4 mb-10">
          {customer.picture && <img src={customer.picture} alt="avatar" className="w-14 h-14 rounded-full border-2 border-[#d4af37]" />}
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">// ACCOUNT</div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#d4af37]">{customer.name}</h1>
            <div className="text-xs text-zinc-500 mt-1">{customer.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logoutCustomer}
            className="btn-raw text-[10px]"
          >
            LOG OUT
          </button>
        </div>

        <div className="flex gap-2 sm:gap-3 mb-8" data-testid="account-tabs">
          <button
            data-testid="tab-orders"
            onClick={() => setTab("orders")}
            className={`px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase font-bold border transition-colors ${
              tab === "orders"
                ? "bg-[#d4af37] border-[#d4af37] text-black"
                : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
            }`}
          >
            Orders ({orders.length})
          </button>
          <button
            data-testid="tab-saved"
            onClick={() => setTab("saved")}
            className={`px-5 py-2.5 text-[11px] tracking-[0.25em] uppercase font-bold border transition-colors ${
              tab === "saved"
                ? "bg-[#d4af37] border-[#d4af37] text-black"
                : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
            }`}
          >
            Saved ({saved.length})
          </button>
        </div>

        {tab === "orders" && (
          <div data-testid="orders-panel">
            {orders.length === 0 ? (
              <div className="raw-panel p-10 text-center">
                <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-6">NO ORDERS YET</div>
                <Link to="/" className="btn-raw btn-raw-primary">→ BROWSE THE SHOP</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id || o.session_id} data-testid={`order-${o.id || o.session_id}`} className="raw-panel p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex-1">
                      <div className="text-white text-sm">{o.product_name || "Order"} {o.size ? `· ${o.size}` : ""} {o.quantity ? `× ${o.quantity}` : ""}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-1">
                        {date(o.paid_at || o.created_at)} · {(o.payment_status || "pending").toUpperCase()}
                      </div>
                    </div>
                    <div className="text-right font-mono text-[#d4af37]">{fmt(o.amount_total || o.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "saved" && (
          <div data-testid="saved-panel">
            {saved.length === 0 ? (
              <div className="raw-panel p-10 text-center">
                <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-6">NO SAVED ITEMS</div>
                <Link to="/" className="btn-raw btn-raw-primary">→ BROWSE THE SHOP</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {saved.map((s) => (
                  <div key={s.id} data-testid={`saved-${s.id}`} className="raw-panel p-3">
                    <Link to={`/product/${s.id}`} className="block aspect-square bg-black border border-[#d4af37]/30 overflow-hidden mb-3">
                      {s.image_base64 ? (
                        <img src={s.image_base64} alt={s.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-[9px] text-zinc-600 tracking-[0.2em] uppercase">NO IMG</div>
                      )}
                    </Link>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={`/product/${s.id}`} className="text-white text-sm hover:text-[#d4af37] truncate block">{s.name}</Link>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mt-1">{fmt(s.price)}</div>
                      </div>
                      <button
                        data-testid={`unsave-${s.id}`}
                        onClick={() => unsave(s.id)}
                        className="text-xs text-zinc-500 hover:text-red-400"
                        aria-label="Remove saved"
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <InfoFooter />
    </div>
  );
};

export default MyAccount;
